import { Link } from 'react-router-dom';
import { Avatar } from '../primitives';
import type { KudosEntry } from '../../fixtures/team';
import {
  E7_TODAY_LINK,
  E7_TODAY_NO_KUDOS,
} from '../../lib/team/athleteTeamCopy';
import styles from './TeamFeed.module.css';

type TeamFeedProps = {
  entries: KudosEntry[];
  /**
   * Phase 7 PR 1 post-smoke fix (2026-05-16) — when set, only the
   * first N entries render and an overflow row links out via
   * `viewAllHref`. Used by the desktop insights row to prevent the
   * card stretching to the full feed height (~750px with 9+ kudos)
   * and breaking row alignment with the CoachCard + LastSession
   * siblings. Mobile section + standalone /v2/team feed leave this
   * unset so the full list still renders there.
   */
  maxEntries?: number;
  /**
   * Destination for the "View all team →" overflow link. When set
   * together with `maxEntries`, a footer row renders below the
   * truncated list. Demo path can leave this undefined (no link
   * needed in the design preview).
   */
  viewAllHref?: string;
  /**
   * UX-S3 (DD-4) — when true and there are no kudos, render a teaser
   * empty-state instead of hiding the feed. Teaches the kudos feature
   * ("send your first kudos") rather than leaving a blank. Callers that
   * still want the section to vanish on empty leave this unset (default).
   */
  showEmptyHint?: boolean;
  /**
   * Team page CD · FRAME 5 · E7 (D5.5), behind `athlete_kudos_v1`.
   *
   * ⚠️ E7 IS THE ONE EMPTY STATE IN THIS VOCABULARY THAT DOES NOT LIVE ON
   * `/team` — it is the founder's screenshotted state, on TODAY. It rides the
   * kudos flag because it is a kudos string; its `<Link>` was already a real
   * link (repaired same-day as B-41), so nothing here is flag-free work.
   *
   * The current block is three elements in a marketing register — a title, a
   * two-clause instruction to feel something (`Cheer on a teammate's run —
   * send your first kudos and celebrate their wins.`) and a link. E-INLINE is
   * ONE line plus one quiet secondary: state what is true, then where it will
   * appear, and never instruct enthusiasm (frame 5 voice rule 1).
   *
   * ⛔ Default false ⇒ this component is byte-identical. `maxEntries` is
   * UNTOUCHED: today-density's ratified `maxEntries={2}` governs this host and
   * is not amended by frame 4's D4.15 cap of 3, which governs `/team`.
   */
  kudosV1?: boolean;
  /**
   * Claude Design return 2026-10-18, item 6. When true, the EMPTY feed prints no link to
   * `/team` ("Open Team →" with `kudosV1`, "Go to Team →" without): the host already carries
   * the one link to Team in its own heading row (2.0 Today's "My Team →"). Every other host
   * leaves this off and keeps its link; a feed with entries is untouched either way. Default
   * false ⇒ byte-identical.
   */
  hideEmptyLink?: boolean;
};

/**
 * Kudos feed — stacked rows inside a single card. Each row: avatar,
 * name + action + optional payload quote, time-ago, optional emoji.
 *
 * If entries is empty, returns null — the section is omitted entirely
 * (handled by the parent route which checks before rendering the label).
 */
export function TeamFeed({
  entries,
  maxEntries,
  viewAllHref,
  showEmptyHint,
  kudosV1,
  hideEmptyLink,
}: TeamFeedProps) {
  if (entries.length === 0) {
    if (!showEmptyHint) return null;
    if (kudosV1) {
      return (
        <div className={styles.card}>
          <div className={styles.empty}>
            {/* Reuses `.emptyBody` deliberately — E-INLINE is one quiet line,
                which is exactly what that rule already is, and reusing it
                leaves `TeamFeed.module.css` untouched (the frame-1 discipline:
                a lane that edits no legacy stylesheet cannot regress one). */}
            <p className={styles.emptyBody}>{E7_TODAY_NO_KUDOS}</p>
            {!hideEmptyLink && (
              <Link to="/team" className={styles.emptyLink}>
                {E7_TODAY_LINK}
              </Link>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className={styles.card}>
        <div className={styles.empty}>
          <p className={styles.emptyTitle}>Team kudos show up here</p>
          <p className={styles.emptyBody}>
            Cheer on a teammate&rsquo;s run. Send your first kudos and
            celebrate their wins.
          </p>
          {/* Future (user idea, UX-S3): for athletes with no team, the AI
              Coaches could send kudos based on usage — captured in MEMORY. */}
          {!hideEmptyLink && (
            <Link to="/team" className={styles.emptyLink}>
              Go to Team →
            </Link>
          )}
        </div>
      </div>
    );
  }
  const visible =
    typeof maxEntries === 'number' && maxEntries > 0
      ? entries.slice(0, maxEntries)
      : entries;
  const remainder = entries.length - visible.length;

  return (
    <div className={styles.card}>
      {visible.map((entry) => (
        <div key={entry.id} className={styles.row}>
          <Avatar initial={entry.initial} bg={entry.avatarBg} size={32} />
          <div className={styles.body}>
            <div className={styles.line}>
              <strong>{entry.name}</strong> {entry.action}
              <span className={styles.time}> · {entry.timeAgo}</span>
            </div>
            {entry.payload && <div className={styles.payload}>{entry.payload}</div>}
          </div>
          {entry.emoji && <div className={styles.emoji}>{entry.emoji}</div>}
        </div>
      ))}
      {remainder > 0 && viewAllHref && (
        <Link to={viewAllHref} className={styles.viewAll}>
          View all team ({entries.length}) →
        </Link>
      )}
    </div>
  );
}
